/** packages/shared-components/src/components/Toast/Toast.tsx */

import React, { useEffect, useRef, useState } from "react";
import { toastImg } from "@eweadn-qiankun/shared-assets";
import { Iconfont } from "../Iconfont";
import "./Toast.css";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastProps {
  /** 标题；缺省时用 message 作为标题 */
  title?: string;
  /** 正文；有 title 时作为描述，无 title 时仅展示为标题 */
  message: string;
  type?: ToastType;
  duration?: number;
  /** 左上角图标；默认 toast.svg */
  iconSrc?: string;
  /** 底部操作按钮文案 */
  actionText?: string;
  onAction?: () => void;
  /** 关闭时回调（关闭按钮 / 操作按钮 / 自动消失） */
  onDismiss?: () => void;
  onClose?: () => void;
}

export const Toast: React.FC<ToastProps> = ({
  title,
  message,
  type = "info",
  duration = 5000,
  iconSrc = toastImg,
  actionText,
  onAction,
  onDismiss,
  onClose,
}) => {
  const [animationState, setAnimationState] = useState<
    "mounting" | "mounted" | "unmounting"
  >("mounting");
  const closingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const onDismissRef = useRef(onDismiss);
  onCloseRef.current = onClose;
  onDismissRef.current = onDismiss;

  const displayTitle = title?.trim() || message;
  const displayBody = title?.trim() ? message : "";

  const beginClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setAnimationState("unmounting");
    onDismissRef.current?.();
    window.setTimeout(() => onCloseRef.current?.(), 280);
  };

  useEffect(() => {
    const timer = requestAnimationFrame(() => {
      requestAnimationFrame(() => setAnimationState("mounted"));
    });
    return () => cancelAnimationFrame(timer);
  }, []);

  useEffect(() => {
    if (duration <= 0) return;
    const timer = window.setTimeout(() => beginClose(), duration);
    return () => window.clearTimeout(timer);
  }, [duration]);

  return (
    <div
      className={`toast toast-${type} toast-${animationState}`}
      role="status"
    >
      <div className="toast-header">
        <div className="toast-header-left">
          <span className="toast-icon" aria-hidden>
            <img
              src={iconSrc}
              alt=""
              className="toast-icon-img"
              draggable={false}
            />
          </span>
          <span className="toast-title">{displayTitle}</span>
        </div>
        <button
          type="button"
          className="toast-close"
          aria-label="close"
          onClick={beginClose}
        >
          <Iconfont icon="close" className="toast-close-icon" />
        </button>
      </div>

      {displayBody ? <p className="toast-body">{displayBody}</p> : null}

      {actionText ? (
        <div className="toast-footer">
          <button
            type="button"
            className="toast-action"
            onClick={() => {
              onAction?.();
              beginClose();
            }}
          >
            {actionText}
          </button>
        </div>
      ) : null}
    </div>
  );
};
